import React, { useEffect, useState, useCallback } from "react";
import { Edit3, Eye, Save, Loader2, Trash2, Plus, RotateCcw, Copy, Cuboid, ChevronDown, ChevronRight, Link2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import ModelViewer3D from "./ModelViewer3D";

/**
 * Model3DEditor — wraps ModelViewer3D with a full CRUD layout editor.
 *
 * Architecture:
 *   - View mode (default): renders ModelViewer3D unchanged.
 *   - Edit mode: side panel exposes the live `layout_3d` JSON as editable rows
 *     for Equipment / Pipes / Pipe Supports / Structural Steel.
 *     User edits, clicks Save → PUT /api/projects/{id}/model3d → backend
 *     rebuilds the .glb and we reload the viewer with a cache-busted URL.
 */
const EQUIPMENT_TYPES = [
  { id: "vertical_vessel", name: "Vertical Vessel / Column", defaults: { diameter: 3.0, height: 12.0 } },
  { id: "horizontal_vessel", name: "Horizontal Vessel / Drum", defaults: { diameter: 2.0, length: 6.0 } },
  { id: "storage_tank", name: "Storage Tank", defaults: { diameter: 8.0, height: 12.0 } },
  { id: "pump", name: "Pump", defaults: { length: 2.5, width: 1.2, height: 1.5 } },
  { id: "heat_exchanger", name: "Heat Exchanger (Shell & Tube)", defaults: { diameter: 1.2, length: 6.0 } },
  { id: "compressor", name: "Compressor", defaults: { length: 5.0, width: 2.5, height: 2.5 } },
  { id: "reactor", name: "Reactor", defaults: { diameter: 4.0, height: 8.0 } },
  { id: "column", name: "Distillation Column", defaults: { diameter: 3.5, height: 25.0 } },
];

const STEEL_TYPES = [
  { id: "column", name: "Steel Column" },
  { id: "beam", name: "Steel Beam" },
  { id: "brace", name: "Cross Brace" },
];

export default function Model3DEditor({ src, focusTag, projectId, runId, layout: initialLayout, onSaved }) {
  const [mode, setMode] = useState("view");
  const [layout, setLayout] = useState(initialLayout || {});
  const [originalLayout, setOriginalLayout] = useState(initialLayout || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [openSection, setOpenSection] = useState({ equipment: true, pipes: true, supports: false, steel: false });
  const [reloadKey, setReloadKey] = useState(0);
  const [connectFrom, setConnectFrom] = useState(null);

  // Sync incoming layout
  useEffect(() => {
    setLayout(initialLayout || {});
    setOriginalLayout(initialLayout || {});
    setDirty(false);
  }, [initialLayout]);

  const equipment = layout.equipment_3d || [];
  const pipes = layout.pipes_3d || [];
  const supports = layout.pipe_supports || [];
  const steel = layout.structural_steel || [];

  const updateEquipment = (idx, patch) => {
    setLayout((l) => {
      const arr = [...(l.equipment_3d || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...l, equipment_3d: arr };
    });
    setDirty(true);
  };
  const updateNested = (idx, key, sub, value) => {
    setLayout((l) => {
      const arr = [...(l.equipment_3d || [])];
      arr[idx] = { ...arr[idx], [key]: { ...(arr[idx][key] || {}), [sub]: value } };
      return { ...l, equipment_3d: arr };
    });
    setDirty(true);
  };
  const removeEquipment = (idx) => {
    setLayout((l) => {
      const arr = [...(l.equipment_3d || [])];
      const removed = arr.splice(idx, 1)[0];
      // Also drop pipes referencing this tag/id
      const pipesArr = (l.pipes_3d || []).filter((p) => p.from !== removed?.tag && p.to !== removed?.tag && p.from !== removed?.id && p.to !== removed?.id);
      return { ...l, equipment_3d: arr, pipes_3d: pipesArr };
    });
    setDirty(true);
  };
  const duplicateEquipment = (idx) => {
    setLayout((l) => {
      const arr = [...(l.equipment_3d || [])];
      const orig = arr[idx];
      const m = String(orig.tag || "EQ-100").match(/^(.*?)(\d+)([A-Za-z]*)$/) || [];
      const [, prefix = "EQ-", numStr = "100", suffix = ""] = m;
      let n = parseInt(numStr, 10) + 1;
      const tags = new Set(arr.map((e) => e.tag));
      while (tags.has(`${prefix}${String(n).padStart(numStr.length, "0")}${suffix}`)) n += 1;
      const newTag = `${prefix}${String(n).padStart(numStr.length, "0")}${suffix}`;
      const clone = JSON.parse(JSON.stringify(orig));
      clone.tag = newTag;
      clone.id = newTag.toLowerCase().replace(/-/g, "_");
      clone.position = { ...(orig.position || {}), x: (orig.position?.x || 0) + 6 };
      arr.push(clone);
      return { ...l, equipment_3d: arr };
    });
    setDirty(true);
  };
  const addEquipment = (type) => {
    const proto = EQUIPMENT_TYPES.find((t) => t.id === type) || EQUIPMENT_TYPES[0];
    const existing = (layout.equipment_3d || []).map((e) => e.tag);
    const prefix = type === "pump" ? "P-" : type === "heat_exchanger" ? "E-" : type === "storage_tank" ? "TK-" : type === "compressor" ? "K-" : type === "reactor" ? "R-" : type === "column" ? "C-" : "V-";
    let n = 101;
    while (existing.includes(`${prefix}${n}`)) n += 1;
    const tag = `${prefix}${n}`;
    const newItem = {
      id: tag.toLowerCase().replace(/-/g, "_"),
      tag,
      type,
      service: proto.name,
      position: { x: 0, y: 0, z: 0 },
      dimensions: { ...proto.defaults },
      label: tag,
    };
    setLayout((l) => ({ ...l, equipment_3d: [...(l.equipment_3d || []), newItem] }));
    setDirty(true);
  };

  const updatePipe = (idx, patch) => {
    setLayout((l) => {
      const arr = [...(l.pipes_3d || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...l, pipes_3d: arr };
    });
    setDirty(true);
  };
  const removePipe = (idx) => {
    setLayout((l) => {
      const arr = [...(l.pipes_3d || [])];
      arr.splice(idx, 1);
      return { ...l, pipes_3d: arr };
    });
    setDirty(true);
  };
  const startConnect = (tag) => {
    if (!connectFrom) { setConnectFrom(tag); return; }
    if (connectFrom === tag) { setConnectFrom(null); return; }
    const existing = (layout.pipes_3d || []).map((p) => p.id);
    let n = 1;
    while (existing.includes(`PIPE-${String(n).padStart(3, "0")}`)) n += 1;
    const pipeId = `PIPE-${String(n).padStart(3, "0")}`;
    setLayout((l) => ({
      ...l,
      pipes_3d: [...(l.pipes_3d || []), { id: pipeId, from: connectFrom, to: tag, diameter_mm: 100, color: "#FFB000" }],
    }));
    setDirty(true);
    setConnectFrom(null);
  };

  const updateSupport = (idx, key, sub, value) => {
    setLayout((l) => {
      const arr = [...(l.pipe_supports || [])];
      arr[idx] = sub ? { ...arr[idx], [key]: { ...(arr[idx][key] || {}), [sub]: value } } : { ...arr[idx], [key]: value };
      return { ...l, pipe_supports: arr };
    });
    setDirty(true);
  };
  const removeSupport = (idx) => {
    setLayout((l) => {
      const arr = [...(l.pipe_supports || [])];
      arr.splice(idx, 1);
      return { ...l, pipe_supports: arr };
    });
    setDirty(true);
  };
  const addSupport = () => {
    const arr = layout.pipe_supports || [];
    const tag = `PS-${arr.length + 1}`;
    setLayout((l) => ({ ...l, pipe_supports: [...arr, { tag, position: { x: 0, y: 0, z: 0 } }] }));
    setDirty(true);
  };

  const updateSteel = (idx, key, sub, value) => {
    setLayout((l) => {
      const arr = [...(l.structural_steel || [])];
      arr[idx] = sub ? { ...arr[idx], [key]: { ...(arr[idx][key] || {}), [sub]: value } } : { ...arr[idx], [key]: value };
      return { ...l, structural_steel: arr };
    });
    setDirty(true);
  };
  const removeSteel = (idx) => {
    setLayout((l) => {
      const arr = [...(l.structural_steel || [])];
      arr.splice(idx, 1);
      return { ...l, structural_steel: arr };
    });
    setDirty(true);
  };
  const addSteel = (type = "column") => {
    const arr = layout.structural_steel || [];
    const tag = `ST-${arr.length + 1}`;
    setLayout((l) => ({
      ...l,
      structural_steel: [...arr, { tag, type, position: { x: 0, y: 0, z: 0 }, dimensions: { length: 0.3, width: 0.3, height: 3.0 } }],
    }));
    setDirty(true);
  };

  const revert = () => {
    setLayout(JSON.parse(JSON.stringify(originalLayout)));
    setDirty(false);
    setErr("");
  };

  const save = useCallback(async () => {
    if (!projectId) return;
    setSaving(true); setErr("");
    try {
      const r = await api.put(`/projects/${projectId}/model3d`, { layout });
      setOriginalLayout(JSON.parse(JSON.stringify(layout)));
      setDirty(false);
      // Force the viewer to reload with the new .glb
      setReloadKey((k) => k + 1);
      onSaved?.(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [projectId, layout, onSaved]);

  const cacheBustedSrc = src ? `${src}${src.includes("?") ? "&" : "?"}v=${reloadKey}` : src;

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }} data-testid="model3d-editor">
      <ModelViewer3D src={cacheBustedSrc} focusTag={focusTag} projectId={projectId} runId={runId} key={reloadKey} />

      {/* Mode toolbar */}
      <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 4, padding: 4, background: "rgba(18,18,18,0.95)", border: "1px solid var(--pf-border)", alignItems: "center", zIndex: 5 }}>
        <button
          className="btn btn-ghost"
          style={{ padding: "6px 10px", borderColor: mode === "view" ? "var(--pf-accent)" : "transparent", color: mode === "view" ? "var(--pf-accent)" : "var(--pf-text)" }}
          onClick={() => setMode("view")}
          data-testid="model3d-mode-view"
        ><Eye size={14}/> View</button>
        <button
          className="btn btn-ghost"
          style={{ padding: "6px 10px", borderColor: mode === "edit" ? "var(--pf-accent)" : "transparent", color: mode === "edit" ? "var(--pf-accent)" : "var(--pf-text)" }}
          onClick={() => setMode("edit")}
          data-testid="model3d-mode-edit"
        ><Edit3 size={14}/> Edit Layout</button>
        {mode === "edit" && (
          <>
            <div style={{ width: 1, height: 20, background: "var(--pf-border)", margin: "0 4px" }}/>
            <button className="btn btn-ghost" style={{ padding: "6px 10px" }} onClick={revert} disabled={!dirty} data-testid="model3d-revert"><RotateCcw size={14}/> Revert</button>
            <button className="btn btn-primary" style={{ padding: "6px 10px" }} onClick={save} disabled={!dirty || saving} data-testid="model3d-save">
              {saving ? <><Loader2 className="pf-spin" size={14}/> Rebuilding…</> : <><Save size={14}/> Save & Rebuild</>}
            </button>
          </>
        )}
      </div>

      {err && <div className="pf-sev pf-sev-error" style={{ position: "absolute", left: 10, top: 60, padding: "6px 10px", zIndex: 6 }} data-testid="model3d-error">{err}</div>}

      {/* Connect-pipe toast */}
      {mode === "edit" && connectFrom && (
        <div data-testid="model3d-connect-toast" style={{ position: "absolute", left: "50%", top: 60, transform: "translateX(-50%)", background: "rgba(18,18,18,0.96)", border: "1px solid var(--pf-accent)", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, zIndex: 6 }}>
          <Link2 size={16} color="#FFB000"/>
          <span className="pf-mono" style={{ fontSize: 12, color: "var(--pf-accent)" }}>CONNECT PIPE · From {connectFrom} → click "Connect" on target equipment</span>
          <button className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11 }} onClick={() => setConnectFrom(null)} data-testid="model3d-connect-cancel">Cancel</button>
        </div>
      )}

      {/* Editor Side Panel */}
      {mode === "edit" && (
        <div style={{ position: "absolute", right: 0, top: 56, bottom: 36, width: 380, background: "var(--pf-surface)", borderLeft: "1px solid var(--pf-border)", boxShadow: "-12px 0 24px rgba(0,0,0,0.35)", zIndex: 4, display: "flex", flexDirection: "column" }} data-testid="model3d-edit-panel">
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>
            <div className="pf-overline" style={{ color: "var(--pf-accent)" }}>3D LAYOUT EDITOR</div>
            <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)", marginTop: 2 }}>
              Edit equipment, pipes, supports, steel. Save to rebuild glTF.
            </div>
            {dirty && <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-accent)", marginTop: 4 }}>● Unsaved changes</div>}
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
            <Section title={`EQUIPMENT (${equipment.length})`} open={openSection.equipment} onToggle={() => setOpenSection((s) => ({ ...s, equipment: !s.equipment }))} testid="model3d-section-equipment">
              <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                <select
                  data-testid="model3d-add-equipment-type"
                  className="pf-input pf-mono"
                  style={{ flex: 1, fontSize: 11, padding: "4px 6px" }}
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) { addEquipment(e.target.value); e.target.value = ""; } }}
                >
                  <option value="" disabled>+ Add equipment…</option>
                  {EQUIPMENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {equipment.map((it, idx) => (
                <EquipmentRow key={`${it.tag}-${idx}`}
                  item={it} idx={idx}
                  onChange={(patch) => updateEquipment(idx, patch)}
                  onChangeNested={(k, s, v) => updateNested(idx, k, s, v)}
                  onDelete={() => removeEquipment(idx)}
                  onDuplicate={() => duplicateEquipment(idx)}
                  onConnect={() => startConnect(it.tag)}
                />
              ))}
              {equipment.length === 0 && <Empty>No equipment yet. Use the dropdown above to add one.</Empty>}
            </Section>

            <Section title={`PIPES (${pipes.length})`} open={openSection.pipes} onToggle={() => setOpenSection((s) => ({ ...s, pipes: !s.pipes }))} testid="model3d-section-pipes">
              {pipes.map((p, idx) => (
                <PipeRow key={`${p.id}-${idx}`} pipe={p} idx={idx} onChange={(patch) => updatePipe(idx, patch)} onDelete={() => removePipe(idx)} />
              ))}
              {pipes.length === 0 && <Empty>No pipes yet. Click "Connect" on two equipment items above to add a pipe.</Empty>}
            </Section>

            <Section title={`PIPE SUPPORTS (${supports.length})`} open={openSection.supports} onToggle={() => setOpenSection((s) => ({ ...s, supports: !s.supports }))} testid="model3d-section-supports">
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11, marginBottom: 8 }} onClick={addSupport} data-testid="model3d-add-support"><Plus size={12}/> Add support</button>
              {supports.map((s, idx) => (
                <SupportRow key={`${s.tag}-${idx}`} item={s} idx={idx} onChange={updateSupport} onDelete={() => removeSupport(idx)} />
              ))}
            </Section>

            <Section title={`STRUCTURAL STEEL (${steel.length})`} open={openSection.steel} onToggle={() => setOpenSection((s) => ({ ...s, steel: !s.steel }))} testid="model3d-section-steel">
              <select
                data-testid="model3d-add-steel-type"
                className="pf-input pf-mono"
                style={{ width: "100%", fontSize: 11, padding: "4px 6px", marginBottom: 8 }}
                defaultValue=""
                onChange={(e) => { if (e.target.value) { addSteel(e.target.value); e.target.value = ""; } }}
              >
                <option value="" disabled>+ Add structural steel…</option>
                {STEEL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {steel.map((s, idx) => (
                <SteelRow key={`${s.tag}-${idx}`} item={s} idx={idx} onChange={updateSteel} onDelete={() => removeSteel(idx)} />
              ))}
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Sub-components ----

function Section({ title, open, onToggle, children, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 10, border: "1px solid var(--pf-border)", borderRadius: 2 }}>
      <button onClick={onToggle} className="pf-overline" style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 10px", background: "var(--pf-surface-2)", border: "none", color: "var(--pf-text)", cursor: "pointer", fontSize: 10, letterSpacing: 1.2, justifyContent: "flex-start" }}>
        {open ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
        {title}
      </button>
      {open && <div style={{ padding: 8 }}>{children}</div>}
    </div>
  );
}

function Empty({ children }) {
  return <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)", padding: "8px 4px" }}>{children}</div>;
}

function NumInput({ value, onChange, label, testid, step = 0.5 }) {
  return (
    <label style={{ display: "block", flex: 1 }}>
      <span className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", display: "block", letterSpacing: 1, marginBottom: 2 }}>{label}</span>
      <input
        type="number"
        step={step}
        className="pf-input pf-mono"
        style={{ width: "100%", padding: "3px 6px", fontSize: 11 }}
        value={value ?? ""}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        data-testid={testid}
      />
    </label>
  );
}

function EquipmentRow({ item, idx, onChange, onChangeNested, onDelete, onDuplicate, onConnect }) {
  const pos = item.position || {};
  const dim = item.dimensions || {};
  const [open, setOpen] = useState(idx === 0); // first item open by default
  return (
    <div style={{ border: "1px solid var(--pf-border-muted)", borderRadius: 2, marginBottom: 6, background: "var(--pf-surface-2)" }} data-testid={`model3d-equipment-${item.tag || idx}`}>
      <div style={{ display: "flex", alignItems: "center", padding: "5px 8px", gap: 6 }}>
        <button onClick={() => setOpen((v) => !v)} style={{ background: "transparent", border: "none", color: "var(--pf-text-muted)", cursor: "pointer", padding: 2 }}>
          {open ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
        </button>
        <Cuboid size={12} color="#FFB000"/>
        <input
          className="pf-input pf-mono"
          style={{ flex: 1, fontSize: 11, padding: "2px 6px" }}
          value={item.tag || ""}
          onChange={(e) => onChange({ tag: e.target.value.trim().toUpperCase() })}
          data-testid={`model3d-eq-tag-${idx}`}
        />
        <button title="Connect pipe" onClick={onConnect} className="btn btn-ghost" style={iconBtn()} data-testid={`model3d-eq-connect-${idx}`}><Link2 size={12}/></button>
        <button title="Duplicate" onClick={onDuplicate} className="btn btn-ghost" style={iconBtn()} data-testid={`model3d-eq-duplicate-${idx}`}><Copy size={12}/></button>
        <button title="Delete" onClick={onDelete} className="btn btn-ghost" style={iconBtn("#E2A0A0")} data-testid={`model3d-eq-delete-${idx}`}><Trash2 size={12}/></button>
      </div>
      {open && (
        <div style={{ padding: 8, borderTop: "1px solid var(--pf-border-muted)" }}>
          <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 4 }}>TYPE: {item.type || "—"} · SERVICE</div>
          <input
            className="pf-input"
            style={{ width: "100%", fontSize: 11, padding: "3px 6px", marginBottom: 8 }}
            placeholder="Service description"
            value={item.service || ""}
            onChange={(e) => onChange({ service: e.target.value })}
            data-testid={`model3d-eq-service-${idx}`}
          />
          <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 3 }}>POSITION (m)</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
            <NumInput label="X" value={pos.x} onChange={(v) => onChangeNested("position", "x", v)} testid={`model3d-eq-x-${idx}`} />
            <NumInput label="Y" value={pos.y} onChange={(v) => onChangeNested("position", "y", v)} testid={`model3d-eq-y-${idx}`} />
            <NumInput label="Z" value={pos.z} onChange={(v) => onChangeNested("position", "z", v)} testid={`model3d-eq-z-${idx}`} />
          </div>
          <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 3 }}>DIMENSIONS (m)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 6 }}>
            <NumInput label="Diameter" value={dim.diameter} onChange={(v) => onChangeNested("dimensions", "diameter", v)} testid={`model3d-eq-diam-${idx}`} />
            <NumInput label="Height" value={dim.height} onChange={(v) => onChangeNested("dimensions", "height", v)} testid={`model3d-eq-height-${idx}`} />
            <NumInput label="Length" value={dim.length} onChange={(v) => onChangeNested("dimensions", "length", v)} testid={`model3d-eq-length-${idx}`} />
            <NumInput label="Width" value={dim.width} onChange={(v) => onChangeNested("dimensions", "width", v)} testid={`model3d-eq-width-${idx}`} />
          </div>
          <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 3 }}>OPERATING</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
            <NumInput label="Press (bar)" value={item.operating_pressure_bar} onChange={(v) => onChange({ operating_pressure_bar: v })} testid={`model3d-eq-press-${idx}`} step={1} />
            <NumInput label="Temp (°C)" value={item.operating_temperature_c} onChange={(v) => onChange({ operating_temperature_c: v })} testid={`model3d-eq-temp-${idx}`} step={1} />
          </div>
          <input
            className="pf-input"
            style={{ width: "100%", fontSize: 11, padding: "3px 6px" }}
            placeholder="Material"
            value={item.material || ""}
            onChange={(e) => onChange({ material: e.target.value })}
            data-testid={`model3d-eq-material-${idx}`}
          />
        </div>
      )}
    </div>
  );
}

function PipeRow({ pipe, idx, onChange, onDelete }) {
  return (
    <div style={{ border: "1px solid var(--pf-border-muted)", borderRadius: 2, padding: 6, marginBottom: 5, background: "var(--pf-surface-2)" }} data-testid={`model3d-pipe-${idx}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-accent)", flex: 1 }}>{pipe.id || `PIPE-${idx + 1}`}</span>
        <button title="Delete pipe" onClick={onDelete} className="btn btn-ghost" style={iconBtn("#E2A0A0")} data-testid={`model3d-pipe-delete-${idx}`}><Trash2 size={12}/></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
        <label style={{ display: "block" }}>
          <span className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", display: "block" }}>FROM</span>
          <input className="pf-input pf-mono" style={{ width: "100%", fontSize: 11, padding: "3px 6px" }} value={pipe.from || ""} onChange={(e) => onChange({ from: e.target.value.trim().toUpperCase() })} data-testid={`model3d-pipe-from-${idx}`}/>
        </label>
        <label style={{ display: "block" }}>
          <span className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", display: "block" }}>TO</span>
          <input className="pf-input pf-mono" style={{ width: "100%", fontSize: 11, padding: "3px 6px" }} value={pipe.to || ""} onChange={(e) => onChange({ to: e.target.value.trim().toUpperCase() })} data-testid={`model3d-pipe-to-${idx}`}/>
        </label>
        <NumInput label="Diameter (mm)" value={pipe.diameter_mm} onChange={(v) => onChange({ diameter_mm: v })} testid={`model3d-pipe-dia-${idx}`} step={25} />
        <label style={{ display: "block" }}>
          <span className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", display: "block" }}>COLOR</span>
          <input type="color" value={pipe.color || "#FFB000"} onChange={(e) => onChange({ color: e.target.value })} data-testid={`model3d-pipe-color-${idx}`} style={{ width: "100%", height: 24, padding: 0, border: "1px solid var(--pf-border)" }}/>
        </label>
      </div>
    </div>
  );
}

function SupportRow({ item, idx, onChange, onDelete }) {
  const pos = item.position || {};
  return (
    <div style={{ border: "1px solid var(--pf-border-muted)", padding: 6, marginBottom: 5, background: "var(--pf-surface-2)" }} data-testid={`model3d-support-${idx}`}>
      <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
        <input className="pf-input pf-mono" style={{ flex: 1, fontSize: 11, padding: "3px 6px" }} value={item.tag || ""} onChange={(e) => onChange(idx, "tag", null, e.target.value.toUpperCase())}/>
        <button onClick={onDelete} className="btn btn-ghost" style={iconBtn("#E2A0A0")} data-testid={`model3d-support-delete-${idx}`}><Trash2 size={12}/></button>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <NumInput label="X" value={pos.x} onChange={(v) => onChange(idx, "position", "x", v)} testid={`model3d-support-x-${idx}`}/>
        <NumInput label="Y" value={pos.y} onChange={(v) => onChange(idx, "position", "y", v)} testid={`model3d-support-y-${idx}`}/>
        <NumInput label="Z" value={pos.z} onChange={(v) => onChange(idx, "position", "z", v)} testid={`model3d-support-z-${idx}`}/>
      </div>
    </div>
  );
}

function SteelRow({ item, idx, onChange, onDelete }) {
  const pos = item.position || {};
  const dim = item.dimensions || {};
  return (
    <div style={{ border: "1px solid var(--pf-border-muted)", padding: 6, marginBottom: 5, background: "var(--pf-surface-2)" }} data-testid={`model3d-steel-${idx}`}>
      <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
        <input className="pf-input pf-mono" style={{ flex: 1, fontSize: 11, padding: "3px 6px" }} value={item.tag || ""} onChange={(e) => onChange(idx, "tag", null, e.target.value.toUpperCase())}/>
        <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>{item.type || "column"}</span>
        <button onClick={onDelete} className="btn btn-ghost" style={iconBtn("#E2A0A0")} data-testid={`model3d-steel-delete-${idx}`}><Trash2 size={12}/></button>
      </div>
      <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 2 }}>POSITION</div>
      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
        <NumInput label="X" value={pos.x} onChange={(v) => onChange(idx, "position", "x", v)} testid={`model3d-steel-x-${idx}`}/>
        <NumInput label="Y" value={pos.y} onChange={(v) => onChange(idx, "position", "y", v)} testid={`model3d-steel-y-${idx}`}/>
        <NumInput label="Z" value={pos.z} onChange={(v) => onChange(idx, "position", "z", v)} testid={`model3d-steel-z-${idx}`}/>
      </div>
      <div className="pf-mono" style={{ fontSize: 9, color: "var(--pf-text-muted)", marginBottom: 2 }}>DIMENSIONS</div>
      <div style={{ display: "flex", gap: 4 }}>
        <NumInput label="L" value={dim.length} onChange={(v) => onChange(idx, "dimensions", "length", v)} testid={`model3d-steel-len-${idx}`}/>
        <NumInput label="W" value={dim.width} onChange={(v) => onChange(idx, "dimensions", "width", v)} testid={`model3d-steel-w-${idx}`}/>
        <NumInput label="H" value={dim.height} onChange={(v) => onChange(idx, "dimensions", "height", v)} testid={`model3d-steel-h-${idx}`}/>
      </div>
    </div>
  );
}

function iconBtn(color) {
  return { padding: 4, color: color || "var(--pf-text)" };
}
